import type {Snapshot} from 'xstate';

import {logger} from '@zoefin/logger';

import type {FhsFormData} from '../types';

import {
  COMPLETED_ANSWERS_KEY,
  COMPLETED_ANSWERS_MAX_AGE_MS,
  CURRENT_ANSWERS_KEY,
  CURRENT_ANSWERS_MAX_AGE_MS,
} from './constants';

interface StoredValue<T> {
  data: T;
  savedAt: number;
}

const readWithExpiry = <T>(key: string, maxAgeMs: number): T | undefined => {
  if (globalThis.window === undefined) return undefined;

  try {
    const raw = globalThis.localStorage.getItem(key);

    if (!raw) return undefined;

    const parsed = JSON.parse(raw) as Partial<StoredValue<T>>;

    if (
      typeof parsed.savedAt !== 'number' ||
      Date.now() - parsed.savedAt > maxAgeMs
    ) {
      globalThis.localStorage.removeItem(key);

      return undefined;
    }

    return parsed.data;
  } catch (error) {
    logger.error(
      {error, key},
      'FHS: failed to read persisted answers from localStorage',
    );

    try {
      globalThis.localStorage.removeItem(key);
    } catch {
      // localStorage unavailable — nothing to clean up.
    }

    return undefined;
  }
};

const writeWithExpiry = <T>(key: string, data: T): void => {
  if (globalThis.window === undefined) return;

  try {
    const payload: StoredValue<T> = {data, savedAt: Date.now()};

    globalThis.localStorage.setItem(key, JSON.stringify(payload));
  } catch (error) {
    logger.error(
      {error, key},
      'FHS: failed to persist answers to localStorage',
    );
    // localStorage may be unavailable (private mode, quota, etc.) —
    // persistence is best-effort.
  }
};

const clearKey = (key: string): void => {
  if (globalThis.window === undefined) return;

  try {
    globalThis.localStorage.removeItem(key);
  } catch (error) {
    logger.error(
      {error, key},
      'FHS: failed to clear persisted answers from localStorage',
    );
  }
};

export const readCurrentAnswersSnapshot = (): Snapshot<unknown> | undefined =>
  readWithExpiry<Snapshot<unknown>>(
    CURRENT_ANSWERS_KEY,
    CURRENT_ANSWERS_MAX_AGE_MS,
  );

export const writeCurrentAnswersSnapshot = (snapshot: unknown): void => {
  writeWithExpiry(CURRENT_ANSWERS_KEY, snapshot);
};

export const clearCurrentAnswers = (): void => clearKey(CURRENT_ANSWERS_KEY);

export const readCompletedAnswers = (): FhsFormData | undefined =>
  readWithExpiry<FhsFormData>(
    COMPLETED_ANSWERS_KEY,
    COMPLETED_ANSWERS_MAX_AGE_MS,
  );

export const writeCompletedAnswers = (formData: FhsFormData): void => {
  writeWithExpiry(COMPLETED_ANSWERS_KEY, formData);
};
